import type React from "react";
import { forwardRef, memo } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/components/podkit/lib/cn";

const variants = cva(
    "group flex flex-row items-center gap-1 rounded-lg border bg-surface-primary py-3.5 pr-4 transition-all",
    {
        variants: {
            variant: {
                readOnly: "border-border-base pl-4 cursor-default",
                editable: "shadow-sm border-border-base hover:border-border-brand-subtle hover:shadow pl-2",
                selected: "border-border-brand-subtle shadow pl-2",
                dragging: "border-border-brand shadow-sm pl-2",
            },
        },
    },
);

// Custom drag handle icon with round filled dots (2x3 grid)
const DragHandleIcon = memo<{ size?: number; className?: string }>(({ size = 14, className }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
    >
        <circle cx="4.5" cy="3" r="1.25" fill="currentColor" />
        <circle cx="9.5" cy="3" r="1.25" fill="currentColor" />
        <circle cx="4.5" cy="7" r="1.25" fill="currentColor" />
        <circle cx="9.5" cy="7" r="1.25" fill="currentColor" />
        <circle cx="4.5" cy="11" r="1.25" fill="currentColor" />
        <circle cx="9.5" cy="11" r="1.25" fill="currentColor" />
    </svg>
));
DragHandleIcon.displayName = "DragHandleIcon";

type AutomationStepNodeProps = React.PropsWithChildren<{
    className?: string;
    onClick?: () => void;
    showDragHandle?: boolean;
}> &
    VariantProps<typeof variants>;

export type AutomationStepNodeVariant = VariantProps<typeof variants>["variant"];

export const AutomationStepNode = forwardRef<HTMLDivElement, AutomationStepNodeProps>(
    ({ children, className, variant, onClick, showDragHandle = false }, ref) => {
        return (
            <div
                ref={ref}
                className={variants({ variant, className })}
                onClick={onClick}
                data-tracking-id={onClick ? "automation-step-node" : undefined}
            >
                {showDragHandle && (
                    <DragHandleIcon
                        size={14}
                        className={cn(
                            "shrink-0 cursor-grab text-content-tertiary transition-colors",
                            "group-hover:text-content-primary",
                        )}
                    />
                )}
                <div className="flex flex-1 flex-col items-start gap-1.5">{children}</div>
            </div>
        );
    },
);

AutomationStepNode.displayName = "AutomationStepNode";
